import type {
  PortalClass,
  PortalDistrict,
  PortalSchool,
  PortalStudent,
} from "@/contracts/portal";
import type {
  PortalAssessmentForTerm,
  PortalReasonCode,
  PortalResultAvailability,
  PortalRiskBand,
  PortalTaskResult,
} from "@/contracts/domain";
import { deriveCompletionStatus, isOverallResultReportable } from "@/lib/report-data";
import { DMT10_2026_TARGET_HIDING_ITEM_IDS, dmt10TargetItemScore } from "@/lib/task-raw-details";
import type { AssessmentForTerm, ClassRoom, District, School, Student } from "@/types";

const SYNTHETIC_RESULT_FIELDS = Symbol("syntheticResultFields");
type ResultField = "availability" | "reasonCodes" | "headlineBand";
type SyntheticResultFieldSource = { [SYNTHETIC_RESULT_FIELDS]?: ReadonlySet<ResultField> };

function availabilityFromEvidence(tasksDone: number, risk: PortalRiskBand): PortalResultAvailability {
  if (tasksDone === 0) return "no_results";
  if (risk === "more_info") return "withheld";
  return "available";
}

function reasonCodesFromAvailability(
  availability: PortalResultAvailability,
  tasksDone: number,
  tasksTotal: number,
): PortalReasonCode[] {
  if (availability === "no_results") return ["insufficient_items"];
  if (availability === "withheld") {
    if (tasksDone < tasksTotal) return ["partial_completion", "insufficient_items"];
    return ["high_uncertainty"];
  }
  return [];
}

function headlineBandFromRisk(
  risk: PortalRiskBand,
  availability: PortalResultAvailability,
): PortalRiskBand | null {
  return availability === "available" ? risk : null;
}

function syntheticFieldsOf(source: object): ReadonlySet<ResultField> | undefined {
  return (source as SyntheticResultFieldSource)[SYNTHETIC_RESULT_FIELDS];
}

function isSyntheticResultField(source: object, field: ResultField): boolean {
  return syntheticFieldsOf(source)?.has(field) ?? false;
}

function markSyntheticResultFields<T extends object>(value: T, fields: ResultField[]): T {
  if (fields.length) {
    Object.defineProperty(value, SYNTHETIC_RESULT_FIELDS, {
      value: new Set(fields),
      enumerable: false,
    });
  }
  return value;
}

function defineResultField(target: object, field: ResultField, value: unknown, enumerable: boolean): void {
  Object.defineProperty(target, field, {
    value,
    enumerable,
    configurable: true,
    writable: true,
  });
}

function carryResultFields(target: object, source: object): void {
  const sourceRecord = source as Record<ResultField, unknown>;
  (["availability", "reasonCodes", "headlineBand"] as const).forEach((field) => {
    const value = sourceRecord[field];
    if (value !== undefined) defineResultField(target, field, value, !isSyntheticResultField(source, field));
  });
}

type LegacyCompositeFields = Partial<{
  enssaCompositeScore: number | null;
  enssaCompositeScoreCi90: { lower: number | null; upper: number | null } | null;
  compositeScore: number | null;
  compositeScoreExact: number | null;
  compositeInterval: [number | null, number | null];
  indexScore: number | null;
  indexScoreExact: number | null;
  indexInterval: [number | null, number | null];
}>;

function readCompositeScore(source: LegacyCompositeFields): number | null {
  return source.enssaCompositeScore ?? source.compositeScore ?? source.indexScore ?? null;
}

function readCompositeScoreExact(source: LegacyCompositeFields): number | null {
  const score = readCompositeScore(source);
  return source.compositeScoreExact ?? source.indexScoreExact ?? score;
}

function readCompositeInterval(source: LegacyCompositeFields): [number | null, number | null] {
  if (source.enssaCompositeScoreCi90) {
    return [source.enssaCompositeScoreCi90.lower, source.enssaCompositeScoreCi90.upper];
  }
  return source.compositeInterval ?? source.indexInterval ?? [null, null];
}

function readSubtestResults(assessment: PortalAssessmentForTerm): Record<string, PortalTaskResult> {
  return assessment.subtestResults ?? assessment.taskResults ?? {};
}

function isDmt10ResultKey(key: string): boolean {
  const normalised = key.toLowerCase();
  return normalised === "dmt10" || normalised === "dmt10_2026";
}

function dmtTargetValidCount(result: PortalTaskResult): number {
  if (!result.itemResponses?.length) return 0;
  return result.itemResponses
    .filter((item) => DMT10_2026_TARGET_HIDING_ITEM_IDS.has(String(item.questionId ?? item.question_id ?? '').trim()))
    .map(dmt10TargetItemScore)
    .filter((score): score is number => score != null)
    .length;
}

function normaliseSubtestResultsForEvidence(results: Record<string, PortalTaskResult>): Record<string, PortalTaskResult> {
  return Object.fromEntries(Object.entries(results).map(([key, result]) => {
    if (!isDmt10ResultKey(key)) return [key, result];
    const validTargets = dmtTargetValidCount(result);
    if (validTargets < 3) {
      return [key, { ...result, completed: false, percentile: null, itemsAttempted: validTargets }];
    }
    return [key, { ...result, itemsAttempted: Math.min(6, validTargets) }];
  }));
}

function ci90FromInterval(interval: [number | null, number | null]): { lower: number | null; upper: number | null } {
  return { lower: interval[0], upper: interval[1] };
}

function mapPortalAssessmentToUi(assessment: PortalAssessmentForTerm): AssessmentForTerm {
  const subtestResults = normaliseSubtestResultsForEvidence(readSubtestResults(assessment));
  const tasksDone = Object.keys(subtestResults).length > 0
    ? Object.values(subtestResults).filter((result) => result.completed).length
    : assessment.tasksDone;
  const compositeScore = readCompositeScore(assessment);
  const availability = isSyntheticResultField(assessment, "availability") ? undefined : assessment.availability;
  const reportable = availability === "available"
    ? compositeScore != null
    : availability === undefined && isOverallResultReportable(tasksDone, assessment.tasksTotal) && compositeScore != null;
  const mapped: AssessmentForTerm = {
    taskResults: subtestResults,
    subtestResults,
    modelledSubtestResults: assessment.modelledSubtestResults,
    tasksDone,
    tasksTotal: assessment.tasksTotal,
    compositeScore: reportable ? compositeScore : null,
    compositeScoreExact: reportable ? readCompositeScoreExact(assessment) : null,
    compositeInterval: reportable ? readCompositeInterval(assessment) : [null, null],
    overallPercentile: reportable ? assessment.overallPercentile : null,
    risk: reportable ? assessment.risk : "more_info",
    status: deriveCompletionStatus(tasksDone, assessment.tasksTotal),
  };
  carryResultFields(mapped, assessment);
  return mapped;
}

function mapAssessmentToPortal(
  assessment: Student["assessments"][keyof Student["assessments"]],
): PortalAssessmentForTerm {
  const syntheticFields: ResultField[] = [];
  const availability = assessment.availability ?? availabilityFromEvidence(assessment.tasksDone, assessment.risk);
  if (assessment.availability === undefined) syntheticFields.push("availability");
  const reasonCodes = assessment.reasonCodes ?? reasonCodesFromAvailability(
    availability,
    assessment.tasksDone,
    assessment.tasksTotal,
  );
  if (assessment.reasonCodes === undefined) syntheticFields.push("reasonCodes");
  const headlineBand = assessment.headlineBand !== undefined
    ? assessment.headlineBand
    : headlineBandFromRisk(assessment.risk, availability);
  if (assessment.headlineBand === undefined) syntheticFields.push("headlineBand");

  return markSyntheticResultFields({
    subtestResults: assessment.subtestResults ?? assessment.taskResults,
    taskResults: assessment.taskResults,
    modelledSubtestResults: assessment.modelledSubtestResults,
    tasksDone: assessment.tasksDone,
    tasksTotal: assessment.tasksTotal,
    enssaCompositeScore: assessment.compositeScore,
    enssaCompositeScoreCi90: ci90FromInterval(assessment.compositeInterval),
    compositeScore: assessment.compositeScore,
    compositeInterval: assessment.compositeInterval,
    overallPercentile: assessment.overallPercentile,
    risk: assessment.risk,
    status: assessment.status,
    availability,
    reasonCodes,
    headlineBand,
  }, syntheticFields);
}

export function mapPortalStudentToUi(student: PortalStudent): Student {
  const assessments = Object.fromEntries(
    Object.entries(student.assessments).map(([term, assessment]) => [term, mapPortalAssessmentToUi(assessment)]),
  ) as Student["assessments"];
  const currentAssessment = assessments["2026-T2"] ?? assessments.T1 ?? Object.values(assessments)[0];
  const summaryAvailability = isSyntheticResultField(student.summary, "availability")
    ? undefined
    : student.summary.availability;
  const summaryDisplayable = summaryAvailability === undefined || summaryAvailability === "available";
  const summaryCompositeScore = currentAssessment?.compositeScore ?? readCompositeScore(student.summary);
  const summaryCompositeInterval = currentAssessment?.compositeInterval ?? readCompositeInterval(student.summary);

  const mapped: Student = {
    id: student.id,
    name: student.name,
    year: student.year,
    cohort: student.cohort,
    sex: student.sex ?? null,
    atsi: student.atsi ?? null,
    isLbote: student.isLbote ?? null,
    tp: {},
    assessments,
    tasksDone: currentAssessment?.tasksDone ?? student.summary.tasksDone,
    tasksTotal: currentAssessment?.tasksTotal ?? student.summary.tasksTotal,
    compositeScore: summaryDisplayable ? summaryCompositeScore : null,
    compositeScoreExact: summaryDisplayable ? currentAssessment?.compositeScoreExact ?? readCompositeScoreExact(student.summary) : null,
    compositeInterval: summaryDisplayable ? summaryCompositeInterval : [null, null],
    overallPercentile: summaryDisplayable ? currentAssessment?.overallPercentile ?? student.summary.overallPercentile : null,
    risk: summaryDisplayable ? currentAssessment?.risk ?? student.summary.risk : "more_info",
    status: currentAssessment?.status ?? student.summary.status,
    _classId: student.context.classId,
    _schoolId: student.context.schoolId,
    _schoolName: student.context.schoolName,
    _districtId: student.context.districtId,
    t1Risk: student.riskTrajectory.t1,
    t3Risk: student.riskTrajectory.t3,
    t4Risk: student.riskTrajectory.t4,
  };
  carryResultFields(mapped, student.summary);
  return mapped;
}

export function mapPortalClassToUi(klass: PortalClass): ClassRoom {
  return {
    id: klass.id,
    name: klass.name,
    teacher: klass.teacher,
    year: klass.year,
    cohort: klass.cohort,
    classRecommendationsUrl: klass.classRecommendationsUrl ?? null,
    classRecommendationRid: klass.classRecommendationRid ?? null,
    classRecommendationFocusTaskIds: klass.classRecommendationFocusTaskIds ?? [],
    students: klass.students.map(mapPortalStudentToUi),
  };
}

export function mapPortalSchoolToUi(school: PortalSchool): School {
  return {
    id: school.id,
    name: school.name,
    leader: school.leader,
    lastAdmin: school.lastAdmin,
    classes: school.classes.map(mapPortalClassToUi),
  };
}

export function mapPortalDistrictToUi(district: PortalDistrict): District {
  return {
    id: district.id,
    name: district.name,
    schools: district.schools.map(mapPortalSchoolToUi),
  };
}

export function mapUiStudentToPortal(student: Student): PortalStudent {
  const syntheticFields: ResultField[] = [];
  const availability = student.availability ?? availabilityFromEvidence(student.tasksDone, student.risk);
  if (student.availability === undefined) syntheticFields.push("availability");
  const reasonCodes = student.reasonCodes ?? reasonCodesFromAvailability(
    availability,
    student.tasksDone,
    student.tasksTotal,
  );
  if (student.reasonCodes === undefined) syntheticFields.push("reasonCodes");
  const headlineBand = student.headlineBand !== undefined
    ? student.headlineBand
    : headlineBandFromRisk(student.risk, availability);
  if (student.headlineBand === undefined) syntheticFields.push("headlineBand");

  return {
    id: student.id,
    name: student.name,
    year: student.year,
    cohort: student.cohort,
    sex: student.sex ?? null,
    atsi: student.atsi ?? null,
    isLbote: student.isLbote ?? null,
    assessments: Object.fromEntries(
      Object.entries(student.assessments).map(([termCode, assessment]) => [
        termCode,
        mapAssessmentToPortal(assessment),
      ]),
    ) as PortalStudent["assessments"],
    summary: markSyntheticResultFields({
      tasksDone: student.tasksDone,
      tasksTotal: student.tasksTotal,
      enssaCompositeScore: student.compositeScore,
      enssaCompositeScoreCi90: ci90FromInterval(student.compositeInterval),
      compositeScore: student.compositeScore,
      compositeInterval: student.compositeInterval,
      overallPercentile: student.overallPercentile,
      risk: student.risk,
      status: student.status,
      availability,
      reasonCodes,
      headlineBand,
    }, syntheticFields),
    context: {
      districtId: student._districtId,
      schoolId: student._schoolId,
      schoolName: student._schoolName,
      classId: student._classId,
    },
    riskTrajectory: {
      t1: student.t1Risk,
      t3: student.t3Risk,
      t4: student.t4Risk,
    },
  };
}

export function mapUiClassToPortal(klass: ClassRoom): PortalClass {
  return {
    id: klass.id,
    name: klass.name,
    teacher: klass.teacher,
    year: klass.year,
    cohort: klass.cohort,
    classRecommendationsUrl: klass.classRecommendationsUrl ?? null,
    classRecommendationRid: klass.classRecommendationRid ?? null,
    classRecommendationFocusTaskIds: klass.classRecommendationFocusTaskIds ?? [],
    students: klass.students.map(mapUiStudentToPortal),
  };
}

export function mapUiSchoolToPortal(school: School): PortalSchool {
  return {
    id: school.id,
    name: school.name,
    leader: school.leader,
    lastAdmin: school.lastAdmin,
    classes: school.classes.map(mapUiClassToPortal),
  };
}

export function mapUiDistrictToPortal(district: District): PortalDistrict {
  return {
    id: district.id,
    name: district.name,
    schools: district.schools.map(mapUiSchoolToPortal),
  };
}
